import { sample } from 'effector';
import {
  campaignStore,
  createUserAutomatically,
  getCampaignSettings,
  getCampaignSettingsFx,
  hideTermsAgreement,
  setCampaignError,
  CampaignError,
} from './campaignStore';
import {
  setPendingQrCode,
  setUser,
  setUserPhone,
  upsertUser,
  userStore,
} from './userStore';
import { initializeTelegramFx, setStartParam, telegramStore } from './telegramStore';
import { setTermsVisible, setTermsAgreed } from './termsStore';
import { setGlobalSessionExpired } from './sessionStore';
import { MainButtonStore, setMainButton } from './mainButtonStore';
import { setSkipQrScan } from './skipQrScan';
import { activationStore } from './activationStore';
import { INVALID_PROMO_LINK_MESSAGE } from '../utils/promoMessages';
import { buildCampaignMainButton } from './campaignMainButton';
import { checkUserExists, checkUserExistsFx, upsertUserFx } from './userStore';
import { createAuthenticatedRequest } from './types';

sample({
  clock: getCampaignSettings,
  source: { user: userStore, telegram: telegramStore },
  filter: ({ user, telegram }) =>
    !!user.user?.id && !user.isCheckingUser && !!telegram.startParam && !!user.telegramInitData,
  fn: ({ user, telegram }) =>
    createAuthenticatedRequest(
      {
        telegramId: user.user!.id.toString(),
        startParam: telegram.startParam!,
      },
      user.telegramInitData
    ),
  target: getCampaignSettingsFx,
});

sample({
  clock: [initializeTelegramFx.done, setUser, setStartParam],
  source: { user: userStore, telegram: telegramStore, campaign: campaignStore },
  filter: ({ user, telegram, campaign }) =>
    !!user.user?.id &&
    !!user.telegramInitData &&
    !user.isCheckingUser &&
    telegram.isInitialized &&
    !!telegram.startParam &&
    !campaign.settings &&
    !campaign.isLoading,
  target: getCampaignSettings,
});

sample({
  clock: createUserAutomatically,
  target: upsertUser,
});

sample({
  clock: hideTermsAgreement,
  target: setTermsVisible.prepend(() => false),
});

sample({
  clock: getCampaignSettingsFx.doneData,
  fn: (settings) => settings.skipQrScan === true,
  target: setSkipQrScan,
});

sample({
  clock: getCampaignSettingsFx.doneData,
  source: userStore,
  filter: (userState, settings) => settings.userExists === false,
  fn: (userState) => {
    const currentUser = userState.user!;
    return {
      username: currentUser.username,
      displayName: `${currentUser.first_name} ${currentUser.last_name || ''}`.trim(),
    };
  },
  target: createUserAutomatically,
});

sample({
  clock: getCampaignSettingsFx.doneData,
  source: userStore,
  filter: (userState, settings) => !settings.userExists && userState.user !== null,
  fn: (userState) => {
    const currentUser = userState.user!;
    return {
      username: currentUser.username,
      displayName: `${currentUser.first_name} ${currentUser.last_name || ''}`.trim(),
    };
  },
  target: createUserAutomatically,
});

sample({
  clock: getCampaignSettingsFx.doneData,
  filter: (settings) => settings.termsConfirm === true,
  target: hideTermsAgreement,
});

sample({
  clock: getCampaignSettingsFx.doneData,
  filter: (settings) => !!settings.skipQrScan && !!settings.deepLinkCode,
  fn: (settings) => settings.deepLinkCode!,
  target: setPendingQrCode,
});

sample({
  clock: getCampaignSettingsFx.doneData,
  filter: (settings) => settings.termsConfirm === true,
  fn: () => true,
  target: setTermsAgreed,
});

sample({
  clock: getCampaignSettingsFx.doneData,
  filter: (settings) => settings.termsConfirm === true,
  target: checkUserExists,
});

sample({
  clock: [
    getCampaignSettingsFx.doneData,
    checkUserExistsFx.doneData,
    upsertUserFx.doneData,
    setUserPhone,
  ],
  source: { user: userStore, campaign: campaignStore, activation: activationStore },
  filter: ({ campaign, activation }) =>
    !!campaign.settings &&
    campaign.settings.isActive === true &&
    campaign.settings.termsConfirm === true &&
    !activation.showRewardCongratulations,
  fn: ({ user, campaign }) => buildCampaignMainButton(campaign.settings!, user)!,
  target: setMainButton,
});

sample({
  clock: getCampaignSettingsFx.failData,
  fn: (error: CampaignError) => {
    const message = error.message || INVALID_PROMO_LINK_MESSAGE;
    window.Telegram?.WebApp?.showAlert(message);
    return message;
  },
  target: setCampaignError,
});

sample({
  clock: getCampaignSettingsFx.failData,
  filter: (error: CampaignError) => error.isSessionExpired === true,
  fn: () => true,
  target: setGlobalSessionExpired,
});
